import { GenerationSkeleton } from '@components/generation/generation.skeleton';
import {
  GenerationList,
  GenerationRow
} from '@components/generation/generation-row';
import { EmptyBoxIcon } from '@components/icons/EmptyBoxIcon';
import { ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useGenerations } from '@/hooks/queries/generations/useGenerations';

type GenerationsSectionProps = {
  workspaceId: string;
  processId: string;
};

export const GenerationsSection = ({
  workspaceId,
  processId
}: GenerationsSectionProps) => {
  const navigate = useNavigate();

  const { generations, isLoading } = useGenerations({
    processId,
    take: 3,
    withCount: false,
    filters: {
      workspace_id: { values: [workspaceId], operator: 'is' },
      version_group: { values: [], operator: 'is_any_of' },
      created_by: { values: [], operator: 'is_any_of' }
    }
  });

  const renderContent = () => {
    if (isLoading) {
      return (
        <GenerationList>
          {Array.from({ length: 3 }).map((_, index) => (
            <GenerationSkeleton
              index={index}
              isFirst={index === 0}
              isLast={index === 2}
              key={index}
            />
          ))}
        </GenerationList>
      );
    }

    if (generations.length === 0) {
      return (
        <div className="flex flex-col items-center gap-8 py-6">
          <EmptyBoxIcon className="size-16" />
          <p className="text-center text-muted-foreground text-sm">
            This process has not been generated yet
          </p>
        </div>
      );
    }

    return (
      <GenerationList>
        {generations.map((generation, index) => (
          <GenerationRow
            generation={generation}
            isFirst={index === 0}
            isLast={index === generations.length - 1}
            key={generation.id}
            to={`/${processId}/generation/${generation.id}`}
          />
        ))}
      </GenerationList>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-balance font-semibold text-lg">
          Recent generations
        </h2>
        <Button
          onClick={() => {
            const params = new URLSearchParams({
              workspace_id: workspaceId,
              workspace_id_op: 'is_any_of'
            });
            navigate(`/${processId}/generation?${params.toString()}`);
          }}
          size="sm"
          variant="outline"
        >
          View all generations
          <ArrowRight />
        </Button>
      </div>

      {renderContent()}
    </div>
  );
};
